'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';

const SOCIAL_LINKS = [
  {
    key: 'instagram',
    href: 'https://www.instagram.com/greenpro.br/',
    className: 'text-white/72 hover:text-white',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.9 1.35a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    key: 'x',
    href: 'https://x.com/greenprobr',
    className: 'text-white/72 hover:text-white',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M18.244 2H21.5l-7.11 8.128L22.75 22h-6.547l-5.126-6.692L5.22 22H1.96l7.605-8.693L1.55 2h6.713l4.633 6.103L18.244 2Zm-1.142 18.044h1.803L7.279 3.86H5.344l11.758 16.184Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    key: 'youtube',
    href: 'https://www.youtube.com/@GreenProbr',
    className: 'text-white/72 hover:text-white',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M21.58 7.19a2.96 2.96 0 0 0-2.08-2.1C17.67 4.6 12 4.6 12 4.6s-5.67 0-7.5.49a2.96 2.96 0 0 0-2.08 2.1C2 9.03 2 12 2 12s0 2.97.42 4.81a2.96 2.96 0 0 0 2.08 2.1c1.83.49 7.5.49 7.5.49s5.67 0 7.5-.49a2.96 2.96 0 0 0 2.08-2.1C22 14.97 22 12 22 12s0-2.97-.42-4.81ZM10.2 15.02V8.98L15.5 12l-5.3 3.02Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    key: 'telegram',
    href: 'https://t.me/greenprobr',
    className: 'text-white/72 hover:text-white',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M21.22 4.52a1.6 1.6 0 0 0-1.64-.21L3.18 11.35c-.79.34-.74 1.49.08 1.76l3.93 1.32 1.52 4.74c.24.76 1.2.93 1.67.3l2.18-2.91 4.28 3.16c.65.48 1.58.12 1.73-.68l2.88-13.79c.08-.39-.07-.79-.39-1.03Zm-4.34 3.2-7.54 6.81a.75.75 0 0 0-.22.41l-.3 2.3-.94-2.92 8.06-6.6a.375.375 0 1 1 .94 0Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
];

export default function AppFooter() {
  const { t } = useLanguage();
  const pathname = usePathname();

  if (pathname === '/calculators/surebet' || pathname === '/monitor/prelive/surebet-calculator') {
    return null;
  }

  return (
    <footer className="app-layout-footer border-t border-white/10 bg-[#0a0a0f] px-4 py-5 sm:px-8">
      <div className="mx-auto max-w-6xl text-xs text-[hsl(var(--muted-foreground))]">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              href="/termos-de-servico"
              className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] px-2.5 py-1 text-[10px] font-medium text-white transition-all duration-200 hover:border-green-500/50 hover:bg-green-500/10 hover:text-green-300"
            >
              {t('footer.terms')}
            </Link>
            <Link
              href="/politica-de-privacidade"
              className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] px-2.5 py-1 text-[10px] font-medium text-white transition-all duration-200 hover:border-green-500/50 hover:bg-green-500/10 hover:text-green-300"
            >
              {t('footer.privacy')}
            </Link>
          </div>

          <div className="flex items-center gap-3 text-white/75">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.key}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t(`footer.social.${social.key}`)}
                className={`inline-flex h-5 w-5 items-center justify-center transition-colors duration-200 ${social.className}`}
              >
                <span aria-hidden="true">{social.icon}</span>
                <span className="sr-only">{t(`footer.social.${social.key}`)}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:items-start">
          <div className="space-y-3">
            <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-2.5 sm:p-3">
              <div className="inline-flex items-center text-[9px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                {t('footer.warningTitle')}
              </div>
              <p className="mt-1 text-[10px] leading-relaxed text-white/80 sm:max-w-2xl">
                {t('footer.warningBody')}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[9px] leading-relaxed text-white/35">
              <p className="inline-flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 512 512" fill="none" aria-hidden="true">
                  <path
                    d="M296.4 164.8c-13.3-48.4-55.4-83.7-106.2-83.7-62.4 0-113 50.6-113 113 0 8.3.9 16.4 2.6 24.2C43.5 226.1 8 267.2 8 317c0 55.2 44.8 100 100 100h288c44.2 0 80-35.8 80-80 0-39.5-28.7-72.3-66.4-78.7-1.3-32.7-16.7-61.8-40.5-82.1l-40.8 101.5 40.8 101.5"
                    fill="#F4811F"
                  />
                  <path
                    d="M269.3 315.7l11.6-39.7s13.3 2.2 21.2-6.7c7.9-8.9 5.6-19.4 5.6-19.4l38.2-130.5s-62.3-19.4-100.5 19.4c-38.2 38.8-16.8 96.9-16.8 96.9l-38.2 79.9 79.3.1z"
                    fill="#FAAE40"
                  />
                </svg>
                <span>Protected by Cloudflare</span>
              </p>
              <p className="inline-flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                  <circle cx="32" cy="32" r="32" fill="#4A90D9" />
                  <path
                    d="M44 24c-1.8-6.6-7.6-11.5-14.6-11.5-8.6 0-15.5 6.9-15.5 15.5 0 1.1.1 2.2.4 3.3C9.6 32.5 6 37.4 6 43c0 7.6 6.2 13.8 13.8 13.8h24.9c6.1 0 11-4.9 11-11 0-5.4-3.9-9.9-9.1-10.8-.2-4.5-2.3-8.5-5.6-11.3"
                    fill="white"
                  />
                </svg>
                <span>
                  reCAPTCHA ·{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors hover:text-white/60"
                  >
                    Privacy
                  </a>{' '}
                  &amp;{' '}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors hover:text-white/60"
                  >
                    Terms
                  </a>
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 text-[11px] leading-relaxed text-white/75 lg:items-end lg:pt-0.5">
            <p className="text-white/80 lg:text-right">
              {t('footer.copyright')} <span className="font-semibold text-green-400">GreenPro</span>{' '}
              - {t('footer.rightsReserved')}
            </p>
            <p className="inline-flex w-fit items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[9px] text-white/60 lg:self-end">
              <span className="text-white/55">{t('footer.developedBy')}</span>
              <span className="font-medium text-white/80">WizSP System Provider</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
